// Phase 6 — Dedicated guidance profile editor.
//
// Replaces the generic ParamField grid for guidance profiles with an
// editor that (a) understands the four guidance laws (PP / PN / APN /
// CLOS) and (b) renders only the parameters relevant to the picked
// law. The seeker block is law-agnostic and is always shown.
//
// The editor is *controlled*: it never mutates the incoming profile
// object. Callers wire `onChange(patch)` and `onSave()` themselves —
// usually the parent page debounces a PATCH /api/sim/guidance-profiles/
// {id} and re-hydrates the form from the server response so audit
// stamps stay clean.
import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const LAWS = [
    { id: "pure_pursuit", label: "Pure Pursuit (PP)" },
    { id: "pn", label: "Proportional Navigation (PN)" },
    { id: "apn", label: "Augmented PN (APN)" },
    { id: "clos", label: "Command Line of Sight (CLOS)" },
];

const SEEKERS = [
    { id: "rf", label: "RF" },
    { id: "ir", label: "IR" },
    { id: "eo", label: "EO" },
    { id: "datalink", label: "Datalink" },
];

// Helper — pull a numeric parameter value out of either a bare number
// or the credibility-stamped {value, ...} envelope.
function pval(v) {
    if (v == null) return "";
    if (typeof v === "number") return v;
    if (typeof v === "object" && "value" in v) return v.value ?? "";
    return v;
}

function NumField({ id, label, value, onChange, step = 0.1, min, max, suffix, testId }) {
    const handle = (e) => {
        const raw = e.target.value;
        if (raw === "") return onChange(null);
        const n = Number(raw);
        if (Number.isFinite(n)) onChange(n);
    };
    return (
        <div className="space-y-1">
            <Label htmlFor={id} className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3">
                {label}
                {suffix ? <span className="ml-1 normal-case text-ux-text-3">({suffix})</span> : null}
            </Label>
            <Input
                id={id}
                type="number"
                step={step}
                min={min}
                max={max}
                value={pval(value)}
                onChange={handle}
                className="h-8 text-[12px] font-mono bg-ux-bg-2"
                data-testid={testId || id}
            />
        </div>
    );
}

export default function GuidanceProfileEditor({
    value,
    onChange,
    readOnly = false,
    testId = "guidance-profile-editor",
}) {
    // Keep an internal mirror so the form stays responsive even if the
    // parent debounces. The parent is the source of truth — every
    // patch we emit also lands locally so re-renders stay flicker-free.
    const [draft, setDraft] = useState(value || {});

    useEffect(() => {
        setDraft(value || {});
    }, [value]);

    const law = draft?.mode || "pn";
    const seeker = draft?.seeker_type || "rf";

    const patch = (partial) => {
        if (readOnly) return;
        const next = { ...draft, ...partial };
        setDraft(next);
        onChange?.(partial);
    };

    const lawSpecific = useMemo(() => {
        switch (law) {
            case "pure_pursuit":
                return (
                    <div
                        className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                        data-testid="guidance-pp-fields"
                    >
                        <NumField
                            id="gp-pp-bias"
                            label="Heading bias"
                            value={draft.pp_heading_bias_deg}
                            onChange={(v) => patch({ pp_heading_bias_deg: v })}
                            suffix="deg"
                            testId="gp-pp-bias"
                        />
                        <NumField
                            id="gp-max-accel"
                            label="Max lateral accel"
                            value={draft.max_lateral_accel_g}
                            onChange={(v) => patch({ max_lateral_accel_g: v })}
                            suffix="g"
                            testId="gp-max-accel"
                        />
                    </div>
                );
            case "pn":
                return (
                    <div
                        className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                        data-testid="guidance-pn-fields"
                    >
                        <NumField
                            id="gp-pn-n"
                            label="Navigation constant N"
                            value={draft.navigation_constant}
                            onChange={(v) => patch({ navigation_constant: v })}
                            step={0.1}
                            min={1}
                            max={6}
                            testId="gp-pn-n"
                        />
                        <NumField
                            id="gp-max-accel"
                            label="Max lateral accel"
                            value={draft.max_lateral_accel_g}
                            onChange={(v) => patch({ max_lateral_accel_g: v })}
                            suffix="g"
                            testId="gp-max-accel"
                        />
                    </div>
                );
            case "apn":
                return (
                    <div
                        className="grid grid-cols-1 sm:grid-cols-3 gap-3"
                        data-testid="guidance-apn-fields"
                    >
                        <NumField
                            id="gp-apn-n"
                            label="Navigation constant N"
                            value={draft.navigation_constant}
                            onChange={(v) => patch({ navigation_constant: v })}
                            step={0.1}
                            min={1}
                            max={6}
                            testId="gp-apn-n"
                        />
                        <NumField
                            id="gp-apn-target-est"
                            label="Target accel est gain"
                            value={draft.apn_target_accel_gain}
                            onChange={(v) => patch({ apn_target_accel_gain: v })}
                            step={0.05}
                            testId="gp-apn-target-est"
                        />
                        <NumField
                            id="gp-max-accel"
                            label="Max lateral accel"
                            value={draft.max_lateral_accel_g}
                            onChange={(v) => patch({ max_lateral_accel_g: v })}
                            suffix="g"
                            testId="gp-max-accel"
                        />
                    </div>
                );
            case "clos":
                return (
                    <div
                        className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                        data-testid="guidance-clos-fields"
                    >
                        <NumField
                            id="gp-clos-beam"
                            label="Beam riding gain"
                            value={draft.clos_beam_gain}
                            onChange={(v) => patch({ clos_beam_gain: v })}
                            step={0.05}
                            testId="gp-clos-beam"
                        />
                        <NumField
                            id="gp-clos-uplink"
                            label="Uplink latency"
                            value={draft.clos_uplink_latency_s}
                            onChange={(v) => patch({ clos_uplink_latency_s: v })}
                            step={0.01}
                            suffix="s"
                            testId="gp-clos-uplink"
                        />
                        <NumField
                            id="gp-max-accel"
                            label="Max lateral accel"
                            value={draft.max_lateral_accel_g}
                            onChange={(v) => patch({ max_lateral_accel_g: v })}
                            suffix="g"
                            testId="gp-max-accel"
                        />
                    </div>
                );
            default:
                return null;
        }
    }, [law, draft]);  // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="space-y-5" data-testid={testId}>
            <section className="space-y-3" data-testid="guidance-law-section">
                <header className="flex items-center justify-between">
                    <h4 className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3">
                        Guidance law
                    </h4>
                </header>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                        <Label className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3">
                            Law
                        </Label>
                        <Select
                            value={law}
                            onValueChange={(v) => patch({ mode: v })}
                            disabled={readOnly}
                        >
                            <SelectTrigger
                                className="h-8 bg-ux-bg-2 text-[12px] font-mono"
                                data-testid="gp-law-select"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                                {LAWS.map((l) => (
                                    <SelectItem key={l.id} value={l.id} className="text-[12px] font-mono">
                                        {l.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                {lawSpecific}
            </section>

            <section className="space-y-3" data-testid="guidance-seeker-section">
                <header className="flex items-center justify-between">
                    <h4 className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3">
                        Seeker
                    </h4>
                </header>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1">
                        <Label className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3">
                            Type
                        </Label>
                        <Select
                            value={seeker}
                            onValueChange={(v) => patch({ seeker_type: v })}
                            disabled={readOnly}
                        >
                            <SelectTrigger
                                className="h-8 bg-ux-bg-2 text-[12px] font-mono"
                                data-testid="gp-seeker-select"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                                {SEEKERS.map((s) => (
                                    <SelectItem key={s.id} value={s.id} className="text-[12px] font-mono">
                                        {s.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <NumField
                        id="gp-seeker-fov"
                        label="FOV half-angle"
                        value={draft.seeker_fov_half_deg}
                        onChange={(v) => patch({ seeker_fov_half_deg: v })}
                        suffix="deg"
                        testId="gp-seeker-fov"
                    />
                    <NumField
                        id="gp-seeker-range"
                        label="Lock-on range"
                        value={draft.seeker_max_range_m}
                        onChange={(v) => patch({ seeker_max_range_m: v })}
                        step={50}
                        suffix="m"
                        testId="gp-seeker-range"
                    />
                </div>
            </section>

            <section className="space-y-3" data-testid="guidance-lockloss-section">
                <header className="flex items-center justify-between">
                    <h4 className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3">
                        Lock-loss policy
                    </h4>
                </header>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <NumField
                        id="gp-reacq"
                        label="Reacquire timeout"
                        value={draft?.lock_loss?.reacquire_timeout_s}
                        onChange={(v) =>
                            patch({
                                lock_loss: { ...(draft.lock_loss || {}), reacquire_timeout_s: v },
                            })
                        }
                        step={0.1}
                        suffix="s"
                        testId="gp-lockloss-reacq"
                    />
                    <NumField
                        id="gp-coast"
                        label="Coast distance"
                        value={draft?.lock_loss?.coast_distance_m}
                        onChange={(v) =>
                            patch({
                                lock_loss: { ...(draft.lock_loss || {}), coast_distance_m: v },
                            })
                        }
                        step={10}
                        suffix="m"
                        testId="gp-lockloss-coast"
                    />
                </div>
            </section>
        </div>
    );
}

export { LAWS as GUIDANCE_LAWS, SEEKERS as SEEKER_TYPES };
